import { useMemo, useState } from "react";
import { fmtRM } from "@/lib/api";
import { waLink, WhatsAppIcon } from "@/lib/whatsapp";

export default function LoanCalculator({ presetPrice = 90000, dark = true }) {
  const [price, setPrice] = useState(presetPrice);
  const [downPct, setDownPct] = useState(10);
  const [rate, setRate] = useState(2.8);
  const [years, setYears] = useState(7);

  const { loan, monthly, interest } = useMemo(() => {
    const loanAmt = price * (1 - downPct / 100);
    const r = rate / 100 / 12;
    const n = years * 12;
    const m = r > 0 ? (loanAmt * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1) : loanAmt / n;
    return { loan: loanAmt, monthly: m, interest: m * n - loanAmt };
  }, [price, downPct, rate, years]);

  const wrap = dark ? "bg-black text-white" : "bg-white text-black border border-black";
  const sub = dark ? "text-neutral-400" : "text-neutral-500";
  const line = dark ? "border-white/15" : "border-black/10";

  const Slider = ({ label, value, min, max, step, onChange, display, testid }) => (
    <div>
      <div className="flex items-center justify-between">
        <label className={`font-mono text-[10px] uppercase tracking-[0.25em] ${sub}`}>{label}</label>
        <span className="font-display text-lg font-bold" data-testid={`${testid}-value`}>{display}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testid}
        className={`aw-range mt-3 w-full ${dark ? "" : "aw-range-light"}`}
      />
    </div>
  );

  return (
    <div data-testid="loan-calculator" className={`${wrap} p-6 md:p-10`}>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-60">First-car friendly</div>
      <h3 className="mt-2 font-display text-3xl font-extrabold uppercase tracking-tight md:text-4xl">
        Loan Calculator
      </h3>

      <div className="mt-8 grid gap-8 md:grid-cols-2">
        <div className="space-y-8">
          <Slider label="Car Price" value={price} min={30000} max={250000} step={1000} onChange={setPrice} display={fmtRM(price)} testid="calc-price" />
          <Slider label="Down Payment" value={downPct} min={0} max={50} step={1} onChange={setDownPct} display={`${downPct}% · ${fmtRM(price * downPct / 100)}`} testid="calc-down" />
          <Slider label="Interest Rate" value={rate} min={2} max={4.5} step={0.05} onChange={setRate} display={`${rate.toFixed(2)}% p.a.`} testid="calc-rate" />
          <Slider label="Loan Tenure" value={years} min={3} max={9} step={1} onChange={setYears} display={`${years} years`} testid="calc-years" />
        </div>

        <div className={`flex flex-col justify-center border ${line} p-6 md:p-8`}>
          <div className={`font-mono text-[10px] uppercase tracking-[0.3em] ${sub}`}>Estimated monthly</div>
          <div className="mt-2 font-display text-5xl font-extrabold tracking-tight md:text-6xl" data-testid="calc-monthly">
            {fmtRM(Math.round(monthly))}
          </div>
          <div className={`mt-6 space-y-2 border-t ${line} pt-5 text-sm`}>
            <div className="flex justify-between"><span className={sub}>Loan amount</span><span className="font-semibold" data-testid="calc-loan">{fmtRM(Math.round(loan))}</span></div>
            <div className="flex justify-between"><span className={sub}>Total interest</span><span className="font-semibold" data-testid="calc-interest">{fmtRM(Math.round(interest))}</span></div>
            <div className="flex justify-between"><span className={sub}>Down payment</span><span className="font-semibold">{fmtRM(Math.round(price * downPct / 100))}</span></div>
          </div>
          <a
            href={waLink(`Hi AutoWorld! I calculated ~${fmtRM(Math.round(monthly))}/month for a ${fmtRM(price)} car (${downPct}% down, ${years} yrs). Can you quote me your best direct deal?`)}
            target="_blank"
            rel="noreferrer"
            data-testid="calc-whatsapp"
            className={`mt-6 flex items-center justify-center gap-2 py-4 font-mono text-[11px] uppercase tracking-[0.22em] transition-colors ${
              dark ? "bg-white text-black hover:bg-neutral-200" : "bg-black text-white hover:bg-neutral-800"
            }`}
          >
            <WhatsAppIcon className="h-4 w-4" /> Apply via WhatsApp
          </a>
          <p className={`mt-4 font-mono text-[9px] uppercase tracking-[0.2em] ${sub}`}>
            Estimate only. Final rate subject to bank approval.
          </p>
        </div>
      </div>
    </div>
  );
}
